// ============================================================================
// New Accounts — Needs Info data hook (Cowork 02, 2026-06-28).
//
// Two sources, surfaced together on the Needs-Info screen and used to keep
// started-but-incomplete cards on the board:
//
//   1. v_account_open_requirements — one row per OPEN requirement on a
//      materialized SOP/facility. tier 'key' (red, non-dismissable) or
//      'helpful' (amber). Carries owner_role / owner_eid for routing.
//      Visibility relies on the underlying RLS (security_invoker view).
//
//   2. v_new_account_review_queue — non-draft/non-cancelled SOP cards whose
//      CUSTOMER is unsettled (materialize_status = 'needs_review'). Since
//      2026-09-10 the building exists from the card's first save, so these
//      rows carry a facility_id: the queue asks "whose building is this?",
//      not "does this card have a building?". They still can't appear in #1
//      (which only lists OPEN requirements on a settled account), so we union
//      them in with a Resolve (create / link) action.
//
// Flat-fetch + client-group, matching the project's standard data pattern.
// ============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { fetchAmNameMap } from '../../../shared/am-name';
import { errorMessage } from '../../../shared/errors';

export type ReqTier = 'key' | 'helpful';

export interface OpenRequirement {
  sop_id: string;
  facility_id: string | null;
  display_name: string;
  req: string;
  label: string;
  tier: ReqTier;
  owner_role: string | null;
  owner_eid: string | null;
}

export interface ReviewQueueRow {
  sop_id: string;
  display_name: string;
  account_name: string | null;
  facility_name: string | null;
  /** The building the card minted at its first save. NULL only for cards that
   *  predate 2026-09-10, which resolve the old way (the RPC still handles it). */
  facility_id: string | null;
  start_date: string | null;
  am_eid: string | null;
  market_region: string | null;
  stage: string | null;
  created_date: string | null;
}

/** A single account/facility grouping rendered as one card on the Needs-Info
 *  screen: its open requirements, split by tier, plus owner display names. */
export interface NeedsInfoGroup {
  sopId: string;
  facilityId: string | null;
  displayName: string;
  keyReqs: OpenRequirement[];
  helpfulReqs: OpenRequirement[];
  /** Resolved owner display names keyed by eid (for chip subtitles). */
  ownerNames: Map<string, string>;
}

export interface NeedsInfoResult {
  groups: NeedsInfoGroup[];
  reviewQueue: ReviewQueueRow[];
  /** sop_id -> count of OPEN KEY requirements (drives board no-drop). */
  keyCountBySop: Map<string, number>;
  /** facility_id -> count of OPEN KEY requirements. */
  keyCountByFacility: Map<string, number>;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useNeedsInfo(): NeedsInfoResult {
  const [reqs, setReqs] = useState<OpenRequirement[]>([]);
  const [reviewQueue, setReviewQueue] = useState<ReviewQueueRow[]>([]);
  const [ownerNames, setOwnerNames] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const firstLoad = useRef(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (firstLoad.current) setIsLoading(true);
      setError(null);
      try {
        const [reqRes, rqRes] = await Promise.all([
          supabase
            .from('v_account_open_requirements')
            .select('sop_id, facility_id, display_name, req, label, tier, owner_role, owner_eid'),
          supabase
            .from('v_new_account_review_queue')
            .select('sop_id, display_name, account_name, facility_name, facility_id, start_date, am_eid, market_region, stage, created_date'),
        ]);
        if (cancelled) return;
        if (reqRes.error) throw reqRes.error;
        if (rqRes.error) throw rqRes.error;

        const reqRows = (reqRes.data ?? []) as OpenRequirement[];
        const rqRows = (rqRes.data ?? []) as ReviewQueueRow[];

        // Resolve owner display names across both sources.
        const eids = [
          ...reqRows.map((r) => r.owner_eid),
          ...rqRows.map((r) => r.am_eid),
        ];
        const names = await fetchAmNameMap(eids);
        if (cancelled) return;

        setReqs(reqRows);
        setReviewQueue(rqRows);
        setOwnerNames(names);
      } catch (err) {
        if (cancelled) return;
        console.error('[needs-info] load failed', err);
        setError(errorMessage(err, 'Failed to load Needs-Info data.'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          firstLoad.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const groups = useMemo<NeedsInfoGroup[]>(() => {
    const bySop = new Map<string, NeedsInfoGroup>();
    for (const r of reqs) {
      let g = bySop.get(r.sop_id);
      if (!g) {
        g = {
          sopId: r.sop_id,
          facilityId: r.facility_id,
          displayName: r.display_name,
          keyReqs: [],
          helpfulReqs: [],
          ownerNames,
        };
        bySop.set(r.sop_id, g);
      }
      if (r.tier === 'key') g.keyReqs.push(r);
      else g.helpfulReqs.push(r);
    }
    // KEY-first ordering so the most urgent accounts sort to the top.
    return Array.from(bySop.values()).sort((a, b) => {
      if (b.keyReqs.length !== a.keyReqs.length) return b.keyReqs.length - a.keyReqs.length;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [reqs, ownerNames]);

  const keyCountBySop = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of reqs) {
      if (r.tier !== 'key') continue;
      m.set(r.sop_id, (m.get(r.sop_id) ?? 0) + 1);
    }
    return m;
  }, [reqs]);

  const keyCountByFacility = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of reqs) {
      if (r.tier !== 'key' || !r.facility_id) continue;
      m.set(r.facility_id, (m.get(r.facility_id) ?? 0) + 1);
    }
    return m;
  }, [reqs]);

  return useMemo(
    () => ({
      groups,
      reviewQueue,
      keyCountBySop,
      keyCountByFacility,
      isLoading,
      error,
      refetch,
    }),
    [groups, reviewQueue, keyCountBySop, keyCountByFacility, isLoading, error, refetch],
  );
}

/** Resolve the sop_resolve_review RPC (create new account / link existing). */
export async function resolveReviewCard(
  action: 'create_account' | 'link_existing',
  sopId: string,
  targetAccountId?: string,
): Promise<void> {
  const { error } = await supabase.rpc('sop_resolve_review', {
    p_action: action,
    p_sop_id: sopId,
    ...(targetAccountId ? { p_target_account_id: targetAccountId } : {}),
  });
  if (error) throw error;
}
